import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { errMsg, fmtDate, fmtDateTime, fmtQty } from "./mcfUtils";

function Section({ title, total, children, testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-baseline justify-between mb-1.5">
        <h3 className="font-semibold text-sm">{title}</h3>
        <span className="text-xs text-muted-foreground">Total: <b className="font-mono">{fmtQty(total)}</b></span>
      </div>
      <div className="overflow-x-auto rounded-md border">{children}</div>
    </div>
  );
}

const Empty = ({ cols, text }) => <TableRow><TableCell colSpan={cols} className="text-center text-xs text-muted-foreground py-3">{text}</TableCell></TableRow>;

export function McfSkuTraceDialog({ warehouse, skuId, isAdmin, onClose, onChanged }) {
  const [d, setD] = useState(null);

  const load = useCallback(async () => {
    try {
      setD((await axios.get(`${API}/mcf-inventory/${warehouse.id}/sku/${encodeURIComponent(skuId)}`)).data);
    } catch (err) {
      toast.error(errMsg(err, "Could not load SKU sources"));
      onClose();
    }
  }, [warehouse.id, skuId, onClose]);

  useEffect(() => { setD(null); if (skuId) load(); }, [skuId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!skuId) return null;

  const voidAdj = async (a) => {
    const reason = window.prompt(`Delete adjustment of ${a.qty} units? Enter a reason (kept in audit history):`);
    if (!reason) return;
    try {
      await axios.post(`${API}/mcf-inventory/adjustments/${a.id}/void`, { reason });
      toast.success("Adjustment deleted");
      load();
      onChanged();
    } catch (err) {
      toast.error(errMsg(err, "Could not delete adjustment"));
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="mcf-sku-trace-dialog">
        <DialogHeader>
          <DialogTitle>{d?.sku_name || skuId} <span className="font-mono text-sm text-muted-foreground">({skuId}) · {warehouse.warehouse_name}</span></DialogTitle>
        </DialogHeader>
        {!d ? <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Loading sources…</p> : (
          <div className="space-y-5">
            <p className="text-sm rounded-md bg-slate-50 px-3 py-2 font-mono" data-testid="mcf-trace-formula">
              GRN {fmtQty(d.grn_qty)} + Adj {fmtQty(d.adjustment_qty)} − Consumed {fmtQty(d.consumed_qty)} = Available {fmtQty(d.available_qty)}
            </p>
            {!d.mapped && <p className="text-sm text-purple-800 bg-purple-50 border border-purple-200 rounded-md px-3 py-2">This line could not be matched to the Products master by SKU ID or Item Code. Correct the SKU on the source quotation to map it.</p>}
            <Section title="Received — MCF Stock Transfers (GRN qty, any status)" total={d.grn_qty} testId="mcf-trace-transfers">
              <Table><TableHeader><TableRow><TableHead>Transfer Invoice</TableHead><TableHead>Status</TableHead><TableHead>Invoice Date</TableHead><TableHead className="text-right">Sent</TableHead><TableHead className="text-right">GRN counted</TableHead></TableRow></TableHeader>
                <TableBody>{d.transfers.length ? d.transfers.map((t) => (
                  <TableRow key={t.line_id || t.transfer_id}><TableCell className="font-medium">{t.invoice_number}</TableCell><TableCell>{t.status}</TableCell><TableCell>{fmtDate(t.invoice_date)}</TableCell><TableCell className="text-right">{fmtQty(t.sent_qty)}</TableCell><TableCell className="text-right font-semibold">{fmtQty(t.grn_qty)}</TableCell></TableRow>
                )) : <Empty cols={5} text="No received GRN" />}</TableBody></Table>
            </Section>
            <Section title="Consumed — uploaded invoices (sale + freebie lines)" total={d.consumed_qty} testId="mcf-trace-invoices">
              <Table><TableHeader><TableRow><TableHead>Quotation</TableHead><TableHead>Invoice No.</TableHead><TableHead>Invoice Date</TableHead><TableHead>Line</TableHead><TableHead>Item Code</TableHead><TableHead className="text-right">Qty</TableHead></TableRow></TableHeader>
                <TableBody>{d.invoices.length ? d.invoices.map((i) => (
                  <TableRow key={`${i.quotation_id}-${i.line_type}-${i.line_index}`}><TableCell className="font-medium">{i.quotation_number}</TableCell><TableCell>{i.invoice_number || "—"}</TableCell><TableCell>{fmtDate(i.invoice_date)}</TableCell><TableCell className="capitalize">{i.line_type}</TableCell><TableCell className="font-mono text-xs">{i.item_code || "—"}</TableCell><TableCell className="text-right font-semibold">{fmtQty(i.qty)}</TableCell></TableRow>
                )) : <Empty cols={6} text="No invoiced consumption" />}</TableBody></Table>
            </Section>
            <Section title="Manual adjustments" total={d.adjustment_qty} testId="mcf-trace-adjustments">
              <Table><TableHeader><TableRow><TableHead>When</TableHead><TableHead>By</TableHead><TableHead>Reason</TableHead><TableHead className="text-right">Qty</TableHead><TableHead /></TableRow></TableHeader>
                <TableBody>
                  {d.adjustments.length ? d.adjustments.map((a) => (
                    <TableRow key={a.id} data-testid={`mcf-trace-adj-${a.id}`}><TableCell className="whitespace-nowrap">{fmtDateTime(a.created_at)}</TableCell><TableCell>{a.created_by}</TableCell><TableCell>{a.reason}</TableCell><TableCell className="text-right font-semibold">{a.qty > 0 ? "+" : ""}{fmtQty(a.qty)}</TableCell>
                      <TableCell className="text-right">{isAdmin && <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600" onClick={() => voidAdj(a)} data-testid={`mcf-trace-adj-delete-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}</TableCell></TableRow>
                  )) : <Empty cols={5} text="No active adjustments" />}
                  {d.voided_adjustments.map((a) => (
                    <TableRow key={a.id} className="opacity-60"><TableCell className="whitespace-nowrap line-through">{fmtDateTime(a.created_at)}</TableCell><TableCell>{a.created_by}</TableCell><TableCell>{a.reason} <span className="text-xs text-rose-700">— deleted by {a.voided_by} ({a.void_reason})</span></TableCell><TableCell className="text-right line-through">{fmtQty(a.qty)}</TableCell><TableCell /></TableRow>
                  ))}
                </TableBody></Table>
            </Section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
